"use client";

import LibraryTypeIcon from "./LibraryTypeIcon";
import {
  LIBRARY_TYPE_KEYS,
  LIBRARY_TYPE_LABEL,
  type LibrarySortMode,
  type LibraryViewMode,
} from "@/lib/libraryFormatters";
import { LIFE_AREA_KEYS, LIFE_AREA_META } from "@/lib/lifeAreas";

interface Props {
  search: string;
  onSearch: (value: string) => void;
  view: LibraryViewMode;
  onViewChange: (next: LibraryViewMode) => void;
  sort: LibrarySortMode;
  onSortChange: (next: LibrarySortMode) => void;
  filterArea: string;
  onAreaChange: (next: string) => void;
  filterType: string;
  onTypeChange: (next: string) => void;
  filterRecommended: boolean;
  onRecommendedToggle: () => void;
  areaCounts: Record<string, number>;
  typeCounts: Record<string, number>;
  /** BUG_EVIDENCE_PACKET_2026-05-25 B-LIB-01: direct create entry
   *  point. When provided, renders an "Add reference" button next to
   *  the search input that opens the LibraryCreateModal. */
  onAddReference?: () => void;
}

export default function LibraryToolbar({
  search,
  onSearch,
  view,
  onViewChange,
  sort,
  onSortChange,
  filterArea,
  onAreaChange,
  filterType,
  onTypeChange,
  filterRecommended,
  onRecommendedToggle,
  areaCounts,
  typeCounts,
  onAddReference,
}: Props) {
  return (
    <div
      data-testid="library-toolbar"
      className="space-y-3 rounded-2xl border border-border bg-card px-4 py-3"
    >
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          data-testid="library-search"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          placeholder="Search saved articles, notes, links…"
          className="min-w-0 flex-1 rounded-full border border-border bg-bg px-4 py-2 text-sm text-text placeholder:text-muted focus:border-forest-med/40 focus:outline-none"
        />

        <div
          role="group"
          aria-label="View mode"
          className="inline-flex overflow-hidden rounded-full border border-border bg-bg"
        >
          <button
            type="button"
            data-testid="library-view-grid"
            onClick={() => onViewChange("grid")}
            className={`px-3 py-1.5 text-xs font-medium transition-colors ${
              view === "grid"
                ? "bg-forest-med text-white"
                : "text-text-light hover:text-forest-med"
            }`}
          >
            Grid
          </button>
          <button
            type="button"
            data-testid="library-view-list"
            onClick={() => onViewChange("list")}
            className={`px-3 py-1.5 text-xs font-medium transition-colors ${
              view === "list"
                ? "bg-forest-med text-white"
                : "text-text-light hover:text-forest-med"
            }`}
          >
            List
          </button>
        </div>

        <select
          data-testid="library-sort"
          value={sort}
          onChange={(e) => onSortChange(e.target.value as LibrarySortMode)}
          className="rounded-full border border-border bg-bg px-3 py-1.5 text-xs font-medium text-text focus:border-forest-med/40 focus:outline-none"
        >
          <option value="recent">Recent</option>
          <option value="alpha">A–Z</option>
          <option value="area">By area</option>
        </select>

        {onAddReference && (
          <button
            type="button"
            data-testid="library-add-reference"
            onClick={onAddReference}
            className="rounded-full bg-forest-med px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-forest"
            aria-label="Add reference or thought"
          >
            + Add reference
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted">
          Area
        </span>
        <Chip
          active={filterArea === "all"}
          onClick={() => onAreaChange("all")}
          testId="library-area-all"
        >
          All
        </Chip>
        {LIFE_AREA_KEYS.map((key) => {
          const meta = LIFE_AREA_META[key];
          const count = areaCounts[key] ?? 0;
          return (
            <Chip
              key={key}
              active={filterArea === key}
              onClick={() => onAreaChange(key)}
              testId={`library-area-${key}`}
            >
              <span aria-hidden>{meta.emoji}</span>
              <span className="ml-1 capitalize">{meta.label}</span>
              <ChipCount value={count} active={filterArea === key} />
            </Chip>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted">
          Type
        </span>
        <Chip
          active={filterType === "all"}
          onClick={() => onTypeChange("all")}
          testId="library-type-all"
        >
          All
        </Chip>
        {LIBRARY_TYPE_KEYS.map((typeKey) => {
          const count = typeCounts[typeKey] ?? 0;
          if (count === 0 && filterType !== typeKey) return null;
          return (
            <Chip
              key={typeKey}
              active={filterType === typeKey}
              onClick={() => onTypeChange(typeKey)}
              testId={`library-type-${typeKey}`}
            >
              <LibraryTypeIcon type={typeKey} />
              <span className="ml-1">{LIBRARY_TYPE_LABEL[typeKey]}</span>
              <ChipCount value={count} active={filterType === typeKey} />
            </Chip>
          );
        })}
        <span className="flex-1" />
        <button
          type="button"
          data-testid="library-recommended-only"
          onClick={onRecommendedToggle}
          className={`rounded-full border px-3 py-1 text-[11px] font-semibold transition-colors ${
            filterRecommended
              ? "border-amber bg-sand-pale text-earth"
              : "border-border bg-bg text-text-light hover:border-forest-med/40 hover:text-forest-med"
          }`}
        >
          ★ Recommended only
        </button>
      </div>
    </div>
  );
}

function Chip({
  active,
  onClick,
  testId,
  children,
}: {
  active: boolean;
  onClick: () => void;
  testId?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`inline-flex items-center rounded-full border px-3 py-1 text-[11px] font-medium transition-colors ${
        active
          ? "border-forest-med bg-forest-med text-white"
          : "border-border bg-bg text-text-light hover:border-forest-med/40 hover:text-forest-med"
      }`}
    >
      {children}
    </button>
  );
}

function ChipCount({ value, active }: { value: number; active: boolean }) {
  if (value <= 0) return null;
  return (
    <span
      className={`ml-1.5 text-[10px] font-semibold ${
        active ? "text-white/70" : "text-muted"
      }`}
    >
      {value}
    </span>
  );
}
